import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowRight, Calendar, Clock, User, Tag, Search, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  FadeInUp,
  StaggerContainer,
  StaggerItem,
  AnimatedBlobs
} from '../components/AnimationComponents';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Static blog data
const blogPosts = [
  {
    id: '1',
    title: 'How AI is Transforming Small Business Operations in Canada',
    excerpt: 'Discover how Canadian SMBs are leveraging AI to cut costs and compete with larger enterprises.',
    category: 'AI Insights',
    author: 'Michael Chen',
    date: 'March 15, 2026',
    readTime: '8 min',
    image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800',
    featured: true
  },
  {
    id: '2',
    title: 'The Complete Guide to ERP Implementation for SMBs',
    excerpt: 'Everything you need to know about choosing and implementing an ERP system for growth.',
    category: 'Business Automation',
    author: 'Sarah Williams',
    date: 'March 10, 2026',
    readTime: '12 min',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800',
    featured: true
  },
  {
    id: '3',
    title: 'GEO vs SEO: Optimizing for AI Search Engines',
    excerpt: 'Learn how to optimize your content for both traditional and generative search.',
    category: 'Digital Marketing',
    author: 'David Kumar',
    date: 'March 5, 2026',
    readTime: '6 min',
    image: 'https://images.unsplash.com/photo-1432821596592-e2c18b78144f?w=800',
    featured: false
  },
  {
    id: '4',
    title: 'ROI Calculator: Is AI Worth It for Your Business?',
    excerpt: 'A practical framework for calculating AI investment returns.',
    category: 'AI Insights',
    author: 'Michael Chen',
    date: 'Feb 28, 2026',
    readTime: '10 min',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800',
    featured: false
  },
  {
    id: '5',
    title: 'Top 5 AI Automation Tools for Canadian Retailers',
    excerpt: 'Explore the best AI tools transforming retail operations.',
    category: 'Business Automation',
    author: 'Sarah Williams',
    date: 'Feb 20, 2026',
    readTime: '7 min',
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800',
    featured: false
  },
  {
    id: '6',
    title: 'Building a Data-Driven Culture in Your SMB',
    excerpt: 'Transform your organization to make decisions based on data.',
    category: 'Strategy',
    author: 'David Kumar',
    date: 'Feb 15, 2026',
    readTime: '9 min',
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800',
    featured: false
  }
];

const categories = ['All', 'AI Insights', 'Business Automation', 'Digital Marketing', 'Strategy'];

// Category Badge Colors
const categoryColors = {
  'AI Insights': 'bg-blue-100 text-blue-700',
  'Business Automation': 'bg-purple-100 text-purple-700',
  'Digital Marketing': 'bg-green-100 text-green-700',
  'Strategy': 'bg-orange-100 text-orange-700'
};

export const ResourcesPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  
  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPosts = blogPosts.filter(post => post.featured);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubscribing(true);
    try {
      await axios.post(`${API_URL}/newsletter`, { email });
      toast.success('Successfully subscribed!');
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <div data-testid="resources-page">
      {/* Hero */}
      <section className="section-padding bg-[#0A2540] relative overflow-hidden">
        <AnimatedBlobs />
        <div className="container-custom text-center relative z-10">
          <FadeInUp>
            <motion.div 
              className="inline-flex items-center gap-2 bg-[#2563EB]/20 border border-[#2563EB]/30 rounded-full px-4 py-2 mb-6"
              animate={{ scale: [1, 1.02, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <BookOpen className="w-4 h-4 text-[#2563EB]" />
              <span className="text-sm text-white/90">Resources & Insights</span>
            </motion.div>
          </FadeInUp>
          
          <FadeInUp delay={0.2}>
            <h1 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-6">
              Insights for Growing Businesses
            </h1>
          </FadeInUp>
          
          <FadeInUp delay={0.4}>
            <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8">
              Expert advice, industry trends, and practical guides
            </p>
          </FadeInUp>

          {/* Search Bar */}
          <FadeInUp delay={0.6}>
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-14 pl-12 pr-4 rounded-full border-0 bg-white shadow-xl"
              />
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Featured Posts */}
      <section className="section-padding">
        <div className="container-custom">
          <FadeInUp className="mb-12">
            <h2 className="font-outfit text-2xl font-bold text-[#0A2540]">Featured Articles</h2>
          </FadeInUp>
          
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-8" staggerDelay={0.15}>
            {featuredPosts.map((post, index) => (
              <StaggerItem key={post.id}>
                <motion.article 
                  className="group bg-white rounded-2xl overflow-hidden border border-slate-100 hover:shadow-2xl transition-all"
                  whileHover={{ y: -8 }}
                  data-testid={`featured-post-${index}`}
                >
                  <div className="relative aspect-video overflow-hidden">
                    <motion.img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.5 }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className={`text-xs font-medium px-3 py-1 rounded-full ${categoryColors[post.category]}`}>
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 mb-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {post.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {post.readTime}
                      </span>
                    </div>
                    <h3 className="font-outfit text-xl font-semibold text-[#0A2540] mb-3 group-hover:text-[#2563EB] transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-slate-600 mb-4 line-clamp-2">{post.excerpt}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-slate-500" />
                        </div>
                        <span className="text-sm text-slate-600">{post.author}</span>
                      </div>
                      <motion.span 
                        className="text-[#2563EB] font-medium text-sm flex items-center gap-1"
                        whileHover={{ x: 5 }}
                      >
                        Read More <ArrowRight className="w-4 h-4" />
                      </motion.span>
                    </div>
                  </div>
                </motion.article>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* All Posts */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          {/* Category Filter */}
          <FadeInUp className="mb-12">
            <div className="flex flex-wrap gap-3">
              {categories.map((category) => (
                <motion.button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === category
                      ? 'bg-[#2563EB] text-white shadow-lg shadow-[#2563EB]/25'
                      : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  data-testid={`category-filter-${category.toLowerCase().replace(' ', '-')}`}
                >
                  {category}
                </motion.button>
              ))}
            </div>
          </FadeInUp>

          {/* Posts Grid */}
          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <AnimatePresence mode="popLayout">
              {filteredPosts.map((post, index) => (
                <motion.article 
                  key={post.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  className="group bg-white rounded-xl overflow-hidden border border-slate-100 hover:shadow-xl transition-all"
                  data-testid={`blog-post-${index}`}
                >
                  <div className="relative aspect-video overflow-hidden">
                    <motion.img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.5 }}
                    />
                    <div className="absolute top-3 left-3">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${categoryColors[post.category]}`}>
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3 text-xs text-slate-500">
                      <span>{post.date}</span>
                      <span>•</span>
                      <span>{post.readTime}</span>
                    </div>
                    <h3 className="font-outfit text-lg font-semibold text-[#0A2540] mb-2 line-clamp-2 group-hover:text-[#2563EB] transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-sm text-slate-600 line-clamp-2">{post.excerpt}</p>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>

          {filteredPosts.length === 0 && (
            <motion.div 
              className="text-center py-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <p className="text-slate-500">No articles found matching your criteria.</p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="section-padding bg-[#0A2540] relative overflow-hidden">
        <AnimatedBlobs />
        <div className="container-custom text-center relative z-10">
          <FadeInUp>
            <h2 className="font-outfit text-3xl font-bold text-white mb-4">
              Stay Ahead of the Curve
            </h2>
          </FadeInUp>
          
          <FadeInUp delay={0.2}>
            <p className="text-white/70 mb-8 max-w-xl mx-auto">
              Get the latest AI insights and business tips delivered weekly.
            </p>
          </FadeInUp>
          
          <FadeInUp delay={0.4}>
            <form 
              onSubmit={handleSubscribe}
              className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
            >
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 h-14 px-6 rounded-full border-0 focus:ring-2 focus:ring-[#2563EB]"
                data-testid="newsletter-email-input"
              />
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  type="submit"
                  disabled={isSubscribing}
                  className="h-14 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8"
                  data-testid="newsletter-submit-button"
                >
                  {isSubscribing ? 'Subscribing...' : 'Subscribe'}
                </Button>
              </motion.div>
            </form>
          </FadeInUp>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-br from-[#2563EB] to-[#1D4ED8]">
        <div className="container-custom text-center">
          <FadeInUp>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-6">
              Have Questions?
            </h2>
          </FadeInUp>
          
          <FadeInUp delay={0.2}>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              Our team is ready to help you navigate your digital transformation.
            </p>
          </FadeInUp>
          
          <FadeInUp delay={0.4}>
            <Link to="/contact">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                <Button className="bg-white text-[#2563EB] hover:bg-slate-100 rounded-full px-8 py-6 text-lg shadow-xl">
                  Contact Us
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>
            </Link>
          </FadeInUp>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;
